import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useIntl, defineMessages } from 'react-intl';
import { FONT_SIZE_16_PX, FONT_SIZE_39_PX } from '../../popTheme';
import { FilterMenu } from './FilterMenu';
import { useDispatch, useSelector } from 'react-redux';
import { sendGetResourceData } from '../../redux/sagas/getResourceDataSaga';
import { selectResourceData } from '../../redux/reducers/resourceDataReducer';
import {
  CATEGORY,
  getCategoryResources,
  CategorySection,
  CategorySectionList,
} from './ResourceCategory';
import { LoadingBackdrop } from './LoadingBackdrop';
import { selectStudyCode } from 'redux/reducers/session';
import { SearchBar } from './SearchBar';

export const Resources = () => {
  const theme = useTheme();
  theme.typography.h1 = {
    fontSize: '3.813rem',
    [theme.breakpoints.only('xs')]: {
      fontSize: FONT_SIZE_39_PX,
    },
  };
  theme.typography.body2 = {
    fontSize: '1.25rem',
    lineHeight: '1.875rem',
    [theme.breakpoints.only('xs')]: {
      fontSize: FONT_SIZE_16_PX,
      lineHeight: '1.5rem',
    },
  };

  const intl = useIntl();
  const messages = defineMessages({
    homeWelcomeTitle: {
      id: 'home.welcome.title',
      defaultMessage: 'Welcome to POP!',
      description: 'Title for home page',
    },
    homeWelcomeBlurb1: {
      id: 'home.welcome.blurb1',
      defaultMessage:
        'Learning to cope with pain on your own is challenging, but you are not alone!',
      description: 'Welcome blurb for the home page',
    },
    homeWelcomeBlurb2: {
      id: 'home.welcome.blurb2',
      defaultMessage: 'Here are some tools and resources to support your needs.',
      description: 'Welcome blurb for the home page',
    },
    resourceCountLabel: {
      id: 'resource.count.label',
      defaultMessage: '{count} resources available',
      description: 'Label indicating the resource count number',
    },
    resourceFilteredCountLabel: {
      id: 'resource.filtered.count.label',
      defaultMessage: '{count} of {total} resources shown',
      description: 'Label indicating the filtered resource count number',
    },
  });

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(sendGetResourceData());
  }, [dispatch]);

  const resourceData = useSelector(selectResourceData);

  const initialCounts = {
    [CATEGORY.pain]: 0,
    [CATEGORY.worry]: 0,
    [CATEGORY.mood]: 0,
    [CATEGORY.sleep]: 0,
    [CATEGORY.substanceUse]: 0,
  };

  const [counts, setCounts] = useState(initialCounts);

  const [item, setItem] = useState(resourceData?.resources);

  const [filter, setFilter] = useState(null);

  const filterResources = useCallback(
    (filter) => {
      let filteredResource = resourceData?.resources;
      if (filter.mediaType && filter.mediaType.length > 0) {
        filteredResource = filteredResource.filter((item) =>
          filter.mediaType.includes(item.mediaType),
        );
      }
      if (filter.duration) {
        filteredResource = filteredResource.filter((item) =>
          item.duration?.toLowerCase().includes(filter.duration?.toLowerCase()),
        );
      }
      if (filter.activity && filter.activity.length > 0) {
        filteredResource = filteredResource.filter((item) => {
          if (filter.activity.length === 2) {
            return item.isLiked && item.isCompleted;
          } else if (filter.activity.includes('Liked') || filter.activity.includes('Aimé')) {
            return item.isLiked;
          } else if (
            filter.activity.includes('Completed') ||
            filter.activity.includes('Complété')
          ) {
            return item.isCompleted;
          } else return false;
        });
      }
      setItem(filteredResource);
    },
    [resourceData],
  );

  const filterResourcesBySearchTerm = useCallback(
    (searchTerm) => {
      let filteredResources = resourceData?.resources.filter((item) => {
        if (searchTerm === '') {
          return item;
        } else {
          return (
            item.title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            item.tagLine?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            item.keywords?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm?.toLowerCase())
          );
        }
      });
      setItem(filteredResources);
    },
    [resourceData],
  );

  useEffect(() => {
    filter ? filterResources(filter) : setItem(resourceData?.resources);
  }, [resourceData, filter, filterResources]);

  const studyCode = useSelector(selectStudyCode);

  const CATEGORIES = [
    {
      name: CATEGORY.recommended,
      cardData: getCategoryResources(item, CATEGORY.recommended, studyCode),
    },
    {
      name: CATEGORY.pain,
      cardData: getCategoryResources(item, CATEGORY.pain, studyCode),
    },
    {
      name: CATEGORY.mood,
      cardData: getCategoryResources(item, CATEGORY.mood, studyCode),
    },
    {
      name: CATEGORY.worry,
      cardData: getCategoryResources(item, CATEGORY.worry, studyCode),
    },
    {
      name: CATEGORY.sleep,
      cardData: getCategoryResources(item, CATEGORY.sleep, studyCode),
    },
    {
      name: CATEGORY.substanceUse,
      cardData: getCategoryResources(item, CATEGORY.substanceUse, studyCode),
    },
  ];

  const loading = useSelector((state) => state.resourceDataReducer.loading);

  const getCountLabel = (category, resourceCount) => {
    // First update the count if necessary
    if (resourceCount > counts[category]) {
      let newCounts = counts;
      newCounts[category] = resourceCount;
      setCounts(newCounts);
    }
    // Construct the label for the resource count
    if (counts[category] > resourceCount) {
      return intl.formatMessage(messages.resourceFilteredCountLabel, {
        count: resourceCount,
        total: counts[category],
      });
    } else {
      return intl.formatMessage(messages.resourceCountLabel, { count: resourceCount });
    }
  };

  // Support responsive UI
  const [width, setWindowWidth] = useState(0);
  useEffect(() => {
    updateDimensions();

    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  const updateDimensions = () => {
    const width = window.innerWidth;
    setWindowWidth(width);
  };

  const handleSearchTerm = (searchTerm) => {
    // Do the search.
    filterResourcesBySearchTerm(searchTerm);
  };

  return (
    <Box
      sx={{
        [theme.breakpoints.up('sm')]: {
          px: '3.75rem',
          pt: '1.25rem',
        },
        ml: '0.75rem',
      }}
    >
      {loading && <LoadingBackdrop />}
      <Typography
        variant="h1"
        sx={{
          mt: '3rem',
          color: 'text.primary',
        }}
      >
        {intl.formatMessage(messages.homeWelcomeTitle)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: '3.75rem',
          color: 'text.primary',
          [theme.breakpoints.down('sm')]: {
            mt: '1rem',
            ml: '0.25rem',
          },
        }}
      >
        {intl.formatMessage(messages.homeWelcomeBlurb1)}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: '0.75rem',
          color: 'text.primary',
          [theme.breakpoints.down('sm')]: {
            ml: '0.25rem',
          },
        }}
      >
        {intl.formatMessage(messages.homeWelcomeBlurb2)}
      </Typography>
      <Grid
        container
        direction="row"
        wrap="nowrap"
        sx={{
          [theme.breakpoints.up('sm')]: {
            mt: '5.625rem',
            mx: '1.5rem',
          },
          mt: '3.75rem',
        }}
      >
        <Grid item xs={6}>
          {width < 1200 && <SearchBar handleSetSearchTerm={handleSearchTerm} />}
          <FilterMenu setFilter={setFilter} />
        </Grid>
        <Grid item xs={6}>
          {width > 1199 && <SearchBar handleSetSearchTerm={handleSearchTerm} />}
        </Grid>
      </Grid>

      {CATEGORIES.map(
        (category) =>
          ((category.cardData.length > 0 && category.name === CATEGORY.recommended) ||
            category.name !== CATEGORY.recommended) &&
          (width > 1199 ? (
            <CategorySection
              key={category.name}
              categoryName={category.name}
              categoryData={category.cardData}
              countLabel={getCountLabel(category.name, category.cardData.length)}
            />
          ) : (
            <CategorySectionList
              key={category.name}
              categoryName={category.name}
              categoryData={category.cardData}
              countLabel={getCountLabel(category.name, category.cardData.length)}
            />
          )),
      )}
    </Box>
  );
};
